import { type ReactNode, useState } from 'react'

import { Card, SearchInput } from '@/components/ui'
import { downloadRowsCsv } from '@/components/ui/table/exportRows'
import { numberToCurrency as money } from '@/utils'
import { ReasonActionButton } from '../ReasonActionButton'
import type { HoldingRow } from '../types'
import { AccountHeading } from './AccountHeading'
import type { AccountSection } from './detailScope'
import { gapSentence } from './gapSentence'
import { GROUP_DIVIDER } from './groupLayout'
import {
  type HoldingsColumns,
  HoldingsTable,
  holdingsColumnsFor,
} from './HoldingsTable'
import { holdingsExport } from './holdingsExport'

export const SEARCH_AFTER_ROWS = 25

interface HoldingsTabProps {
  sections: AccountSection[]
  showNames: boolean
  slug: string
  renderStrip: (onExport: () => void) => ReactNode
}

const matchesSearch = (holding: HoldingRow, search: string) => {
  const needle = search.trim().toLowerCase()
  return (
    !needle ||
    holding.symbol.toLowerCase().includes(needle) ||
    holding.description.toLowerCase().includes(needle)
  )
}

const HoldingsSummary = ({ section }: { section: AccountSection }) => {
  const statement = section.account.statement
  if (!statement) return null
  const printed = statement.printedHoldingsTotal
  return (
    <span className="flex items-baseline gap-2 text-xs text-ink-subtle">
      {printed == null ? 'Holdings add to' : 'Printed total'}
      <span className="text-sm tabular-nums text-blue-7">
        {money(printed ?? statement.holdingsSum)}
      </span>
    </span>
  )
}

// When the rows do not reach the printed total, the row sum and the button sit under the heading.
const ShortHoldings = ({ section }: { section: AccountSection }) => {
  const statement = section.account.statement
  if (!statement?.countedAtPrintedTotal) return null
  const reason = statement.reasons.find(
    (candidate) => candidate.action.kind === 'open_statement',
  )
  return (
    <div className="mt-2 flex items-center justify-end gap-3">
      <span className="text-xs text-ink-subtle">Holdings add to</span>
      <span className="text-sm tabular-nums">
        {money(statement.holdingsSum)}
      </span>
      {reason && <ReasonActionButton action={reason.action} />}
    </div>
  )
}

const gapLine = (section: AccountSection) => {
  const gap = gapSentence(section.account)
  return section.account.status === 'missing'
    ? `${gap}, so its holdings on that date are not known.`
    : `${gap}.`
}

const HoldingsGroup = ({
  section,
  rows,
  showName,
  columns,
}: {
  section: AccountSection
  rows: HoldingRow[]
  showName: boolean
  columns: HoldingsColumns
}) => {
  if (!section.account.statement) {
    return (
      <AccountHeading
        section={section}
        showName={showName}
        gapSentence={gapLine(section)}
      />
    )
  }
  return (
    <>
      <AccountHeading
        section={section}
        showName={showName}
        summary={<HoldingsSummary section={section} />}
      />
      <ShortHoldings section={section} />
      {rows.length > 0 ? (
        <HoldingsTable holdings={rows} columns={columns} />
      ) : (
        <p className="mt-3 text-sm text-ink-subtle">
          {section.holdings.length > 0
            ? 'No holdings match the search.'
            : 'None found on this statement.'}
        </p>
      )}
    </>
  )
}

// The holdings each account printed on its As-of statement, one plain table per account.
export const HoldingsTab = ({
  sections,
  showNames,
  slug,
  renderStrip,
}: HoldingsTabProps) => {
  const [search, setSearch] = useState('')
  const groups = sections.map((section) => ({
    section,
    rows: section.holdings.filter((holding) => matchesSearch(holding, search)),
  }))
  const allHoldings = sections.flatMap((section) => section.holdings)
  const columns = holdingsColumnsFor(allHoldings)
  const exportCsv = () =>
    downloadRowsCsv(
      holdingsExport(groups.flatMap((group) => group.rows)),
      `${slug}-holdings.csv`,
    )
  return (
    <div className="flex flex-col gap-4">
      {renderStrip(exportCsv)}
      <Card className="p-6 text-sm">
        {allHoldings.length > SEARCH_AFTER_ROWS && (
          <div className="mb-5 flex justify-end">
            <SearchInput
              value={search}
              onChange={setSearch}
              placeholder="Search holdings..."
              className="w-56"
            />
          </div>
        )}
        {groups.map(({ section, rows }, position) => (
          <div
            key={section.account.accountId}
            className={position === 0 ? undefined : GROUP_DIVIDER}
          >
            <HoldingsGroup
              section={section}
              rows={rows}
              showName={showNames}
              columns={columns}
            />
          </div>
        ))}
      </Card>
    </div>
  )
}
